import { observer } from "mobx-react"
import {
  Page,
  Header,
  DocumentTemplate,
  useStore,
  PureLogo,
} from "@features/common"
import {
  DesktopTableOfContents,
  LegalSectionView,
  TermsLogo,
  TouchTableOfContents,
  PreviousVersions,
} from "@features/terms"
import { serverSidePagePrepare } from "@lib/hooks"
import { scrollToTop } from "@lib/scroll"
import { RouterLink } from "@lib/routing"
import { IconHamburger } from "@lib/icons"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { dateFormater } from "@lib/date"
import { redirect } from "@lib/isomorphic"

export const getServerSideProps = serverSidePagePrepare({
  callback: async ({ Models, ctx }) => {
    const { effective_date } = ctx.query
    const queryDate = Array.isArray(effective_date)
      ? effective_date[0]
      : effective_date

    await Models.FaqModel.init()
    try {
      const countryCode = await Models.GlobalModel.getClientCountryCode(ctx)
      await Models.LegalModel.getTermsPageData(queryDate, countryCode)
    } catch {
      redirect(`/${Models.GlobalModel.lang}/not-found`, ctx)
    }
  },
})

const TermsPage = observer(function TermsPage() {
  const {
    GlobalModel: { getTemplate, openMenu },
    LegalModel: {
      termsSections,
      termsTitle,
      termsEffectiveDate,
      termsVersions,
    },
  } = useStore()
  const { asPath } = useRouter()
  const [isMounted, setIsMounted] = useState(false)

  const metaTitle = getTemplate("terms.metaTitle") || termsTitle
  const description = getTemplate("terms.metaDescription")

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    function scrollToAnchor() {
      const hash = asPath.split("#")[1]
      if (hash) {
        const section = document.querySelector(`[data-faq-id=${hash}]`)
        if (section) {
          section.scrollIntoView()
        }
      }
    }
    if (isMounted) {
      setTimeout(() => scrollToAnchor(), 100)
    }
  }, [isMounted, asPath])

  return (
    <Page
      meta={{
        title: termsTitle,
        metaTitle,
        description,
      }}
      header={
        <Header
          theme="dark"
          logo={
            <RouterLink
              uri="/terms"
              onClick={() => scrollToTop()}
              data-test-id="header__pure_logo"
              aria-label="root"
              className="mt-4 mb-3 tablet-m:mt-[20px] tablet-m:mb-[18px]"
            >
              <div className="shrink-0 flex items-stretch h-4 tablet-m:h-7">
                <PureLogo className="shrink-0 block h-full w-auto tablet:h-full text-white pr-2 tablet-m:pr-4" />
                <TermsLogo />
              </div>
            </RouterLink>
          }
          languageSwitcher={null}
          navigationOpener={
            <button
              onClick={openMenu}
              data-test-id="header__menu_button"
              aria-label="menu"
            >
              <IconHamburger className="w-8 h-8 text-white" />
            </button>
          }
        />
      }
      seoH1={termsTitle}
    >
      <div className="container pt-3 pb-15 laptop:pt-8 laptop:pb-20 legal-document">
        {termsSections.length > 1 ? (
          <TouchTableOfContents tableOfContents={termsSections} />
        ) : null}
        <DocumentTemplate
          sidebar={
            termsSections.length > 1 ? (
              <DesktopTableOfContents tableOfContents={termsSections} />
            ) : null
          }
        >
          <section className="space-y-10" id="scrollContainerElement">
            {termsSections.map((i) => (
              <LegalSectionView data={i} key={i.id} id={i.id} />
            ))}
            <div>
              <div className="pb-3">
                <strong>Effective date:</strong>{" "}
                {termsEffectiveDate
                  ? dateFormater(
                      new Date(termsEffectiveDate),
                      "dd MMMM yyyy",
                      "en",
                    )
                  : ""}
              </div>
              <PreviousVersions versions={termsVersions} />
            </div>
          </section>
        </DocumentTemplate>
      </div>
    </Page>
  )
})

export default TermsPage
